/**
 * Easter social posts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterSocialPostsPack: ToilePack = {
    slug: 'easter-social-posts',
    category: 'season',
    name: 'Easter social posts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'greeting', label: 'Greeting post', subject: 'A square post lettered "Happy Easter" in a soft script over a wreath of tulips'},
        {key: 'sale', label: 'Sale post', subject: 'A square post lettered "SPRING SALE" in bold with an egg motif and a blank band for the offer'},
        {key: 'closed', label: 'Closed notice', subject: 'A square post lettered "CLOSED FOR EASTER" with a bunny and blank lines for dates'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel kawaii', style: 'pastel kawaii illustration, rounded shapes, soft outlines, friendly faces', palette: 'Soft pink, mint, lilac, butter yellow and cream', framing: 'Centered on a solid pastel background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: 'victorian easter card illustration, soft halftone grain, gentle colour', palette: 'Faded rose, sage green, cream and a little gold', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal illustration, few flat shapes, no outlines', palette: 'Two or three flat spring colours on white', framing: 'Centered with generous white space around it'},
    ],
    directions: ['as a story version', 'in a brighter pastel palette', 'with a floral corner border'],
    directionHint: 'try: as a story version',
    seo: {
        title: 'Easter Social Posts: Greeting, Sale & Closed Notice',
        metaDescription: 'Nine Easter social media posts — Happy Easter greeting, Spring Sale, Closed for Easter notice — in pastel kawaii, vintage, and modern styles.',
        h1: 'Easter social media post templates: greeting, sale, closed notice',
        tagline: 'Nine posts, three messages, three treatments each.',
        intro: 'Nine square Easter posts for the feed: a Happy Easter greeting over a wreath of tulips, a Spring Sale post with a blank band for the offer, and a Closed for Easter notice with blank date lines, each in a pastel kawaii, a Victorian vintage, and a modern minimal treatment. Lettered and legible, ready to post as-is.',
        useCases: [
            'an Instagram or Facebook feed post',
            "a shop's holiday-hours announcement",
            'a spring sale promotion post',
            'a printed sign matching the online post',
        ],
        faq: [
            {q: 'Can I edit the blank lines and offer band for my own text?', a: 'Yes — the sale and closed-notice designs leave blank space for you to add your own discount or dates in any image editor.'},
            {q: 'What size are the posts formatted for?', a: 'Square format, sized to post directly to Instagram, Facebook, or any feed that displays square images.'},
            {q: "What's the difference between the three styles?", a: 'Pastel kawaii uses rounded, friendly shapes in soft colour, vintage draws on Victorian Easter-card illustration, and modern minimal keeps to a few flat shapes and generous space.'},
            {q: 'Can I use the closed-for-easter design for my actual business hours?', a: "Yes — it's built exactly for that: a legible announcement with room to add your dates."},
        ],
    },
}
